import { useCallback, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ApolloError, type PureQueryOptions, useMutation } from "@apollo/client";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { type AbsoluteRoute, type RouteParams } from "../../core/routes/absoluteRoutes";
import { CofConfigId } from "../../gql-generated/types";
import {
    type CreateOrganizationAndLensSpaceMutation,
    type CreateOrganizationAndLensSpaceMutationVariables,
    createOrganizationAndLensSpace as createOrganizationAndLensSpaceMutation,
} from "../../submit/api/mutation";
import { getAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreation } from "../../submit/api/queries";
import { getOrganizations, getRoles, myLensesOrganizations } from "../api/queries";
import { type ApolloResult } from "../models/ApolloResult";

export type CreatedOrganization = NonNullable<
    CreateOrganizationAndLensSpaceMutation["createMyLensesOrganizationAndLensSpace"]["organization"]
>;

/**
 * One organization-creation flow per mounted hook; the command is void, consumers observe state.
 * `loading` covers the awaited refetch of the org stores AppRoutes validates against. `data` keeps
 * the confirmed org even if that refetch fails (then `error` is set too), and a confirmed creation
 * is never repeated.
 */
export const useCreateOrganizationAndLensSpace = (
    organizationName: string,
    lensSpaceName: string,
): ApolloResult<{ data?: CreatedOrganization; createOrganizationAndLensSpace: () => void }> => {
    const { submissionId } = useParams<RouteParams[AbsoluteRoute.CREATE_ORG_LENS_FOLDER]>();
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;
    // Command lock, not render state: prevents double submission and recreating a saved org.
    const creationStarted = useRef(false);
    // Apollo clears mutation data if an awaited refetch fails; this preserves the confirmation.
    const [data, setData] = useState<CreatedOrganization>();

    const [mutate, { error, loading }] = useMutation<
        CreateOrganizationAndLensSpaceMutation,
        CreateOrganizationAndLensSpaceMutationVariables
    >(createOrganizationAndLensSpaceMutation, {
        errorPolicy: "none",
        awaitRefetchQueries: true,
    });

    const createOrganizationAndLensSpace = useCallback((): void => {
        if (!organizationName || !lensSpaceName || creationStarted.current) {
            return;
        }

        creationStarted.current = true;
        let organization: CreatedOrganization | undefined;
        const refetchQueries: (string | PureQueryOptions)[] = [
            { query: getRoles },
            // A query-options object also fetches when no observer is active (orgs-v2).
            { query: getOrganizations },
            isMyLensesOrgsV2Enabled
                ? { query: myLensesOrganizations, variables: { limit: 0, cursor: "" } }
                : "GetCameraKitOrganizations",
            ...(!isMyLensesOrgsV2Enabled && submissionId
                ? [
                      {
                          query: getAllPresubmissionInfoWithOrgResourcesAndOrgsAvailableForLensSpaceCreation,
                          variables: { submissionId },
                      },
                  ]
                : []),
        ];
        void mutate({
            variables: { organizationName, lensSpaceName },
            refetchQueries: ({ data: response }) => {
                organization = response?.createMyLensesOrganizationAndLensSpace.organization ?? undefined;
                if (!organization) {
                    // Rejects before refetching; Apollo publishes it through `error`.
                    throw new ApolloError({ errorMessage: "The server did not return the created organization." });
                }
                setData(organization);
                return refetchQueries;
            },
        }).catch(() => {
            // Apollo already exposes the error. Allow a retry only if no organization was returned.
            if (!organization) {
                creationStarted.current = false;
            }
        });
    }, [mutate, organizationName, lensSpaceName, isMyLensesOrgsV2Enabled, submissionId]);

    return { createOrganizationAndLensSpace, data, error, loading };
};
